import { POSTApplicantDetails } from '@/apis/family_application/requests.ts'
import { useAppAlert } from '@/app/AppAlertProvider.tsx'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { Input } from '@/common/components/Input.tsx'
import { CommonQueries } from '@/queries/common/queries.ts'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { Label } from '@/shad/ui/label.tsx'
import {
  selectFamilyApplication,
  setIdentityInfo,
} from '@/storage/applications/familyApplicationSlice.ts'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import FamilyWizardLayout from '../FamilyWizardLayout.tsx'

export default function ResidencyCaptureDetails() {
  const { t: translate } = useTranslation()
  const { fireAlert } = useAppAlert()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const residencyManualData = useSelector(selectFamilyApplication)
  const programId = useSelector(selectProgramID)
  const { data: statesData } = useQuery(CommonQueries.FetchStateByContry(3))

  const studentSchema = z.object({
    address: z.string().min(1, 'address_is_required'),
    address2: z.string(),
    city: z.string().min(1, 'city_is_required'),
    zip_code: z.string().min(1, 'zip_code_is_required'),
    global_state_id: z
      .string()
      .min(1, { message: 'state_is_required' })
      .nullable(),
    county: z.string().nullable(),
    isServingInMilitary: z.boolean(),
  })

  type FormValues = z.infer<typeof studentSchema>

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      address: residencyManualData.address ?? undefined,
      address2: residencyManualData.address2 ?? undefined,
      city: residencyManualData.city ?? undefined,
      zip_code: residencyManualData.zip_code ?? undefined,
      county: residencyManualData.county ?? undefined,
      global_state_id:
        residencyManualData.global_state_id?.toString() ?? undefined,
      isServingInMilitary: residencyManualData.is_military_exemption
    },
    resolver: zodResolver(studentSchema),
  })

  const onSubmit = async (data: any) => {
    data.program_id = programId
    data.first_name = residencyManualData?.first_name
    data.last_name = residencyManualData?.last_name
    data.phone = residencyManualData?.phone
    data.is_military_exemption = data.isServingInMilitary ? 1 : 0

    dispatch(setIdentityInfo(data))

    if (data?.address2?.trim().length == 0) delete data.address2
    const response = await POSTApplicantDetails(data)
    if (response.error) {
      await fireAlert({
        variant: 'error',
        message: response.error.message,
      })
      return
    }
    navigate(
      AppRoutePaths.family_application.residency_verification
        .success_verification
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('residency_verification')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-full">
        <form
          id="student-details-form"
          onSubmit={handleSubmit(onSubmit)}
          className="space-y-3"
        >
          <div className="my-10 w-full">
            <div className="flex flex-col mt-6 space-y-2">
              <label
                htmlFor="address"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('address')}{' '}
                <span className="text-danger ml-1">*</span>
              </label>
              <div>
                <Input
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  {...register('address')}
                />
              </div>
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.address.message ?? '')}
                </p>
              )}
            </div>

            <div className="flex flex-col mt-6 space-y-2">
              <label
                htmlFor="address2"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('address_2')}{' '}
              </label>
              <div>
                <Input
                  {...register('address2')}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="flex flex-col mt-6 space-y-2">
              <label
                htmlFor="city"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('city')} <span className="text-danger ml-1">*</span>
              </label>
              <Input {...register('city')} />
            </div>

            <div className="flex flex-col mt-6 space-y-2">
              <label
                htmlFor="state"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('state')} <span className="text-danger ml-1">*</span>
              </label>
              <div>
                <select
                  {...register('global_state_id')}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                >
                  <option value="">{translate('select_state')}</option>
                  {statesData?.data.map((state) => (
                    <option key={state.id} value={state.id}>
                      {state.name}
                    </option>
                  ))}
                </select>
                {errors.global_state_id && (
                  <p className="mt-1 text-sm text-red-600">
                    {translate(errors.global_state_id.message ?? '')}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col mt-6 space-y-2">
              <label
                htmlFor="zip_code"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('zip_code')}{' '}
                <span className="text-danger ml-1">*</span>
              </label>
              <div>
                <Input
                  {...register('zip_code')}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                />
              </div>
              {errors.zip_code && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.zip_code.message ?? '')}
                </p>
              )}
            </div>

            <div className="flex flex-col mt-6 space-y-2">
              <label
                htmlFor="county"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                {translate('county')}{' '}
              </label>
              <div>
                <Input
                  {...register('county')}
                  className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                />
              </div>
              {errors.county && (
                <p className="mt-1 text-sm text-red-600">
                  {translate(errors.county.message ?? '')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 mt-8">
              <input
                id="isServingInMilitary"
                className="h-5 w-5 rounded-md focus:ring-amber-600  border-gray-500 text-amber-600 checked:bg-amber-600"
                type="checkbox"
                {...register('isServingInMilitary')}
              />
              <Label htmlFor="isServingInMilitary" className="text-gray-800">
                {translate('serving_in_military_question')}
              </Label>
            </div>
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        {' '}
        <SubmitButton form="student-details-form" status={'success'} />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
